import { useState, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  CheckCircle2,
  MessageCircle,
  Calendar,
  Clock,
  Monitor,
  Users,
  BookOpen,
  Award,
  ArrowRight,
} from "lucide-react";
import StarRating from "../../Components/LandingPage/UEC2026/StarRating";
import FAQItem from "../../Components/LandingPage/UEC2026/FAQItem";
import Carousel from "../../Components/Carousel";
import RegistrationForm from "../../Components/LandingPage/UEC2026/RegistrationForm";
import { Navbar } from "../../Components/Navbar";
import {
  WA_NUMBER,
  FONT,
  SCHEDULE,
  TEACHERS,
  BENEFITS,
  PAIN_POINTS,
  TESTIMONIALS,
  FAQS,
  SUBJECT_ICONS,
  CLASS_SCREENSHOT,
} from "../../Data/LandingPage/UEC2026Data";

// ── Constants ─────────────────────────────────────────────────────────────────

const WA_LINK = `https://wa.me/${WA_NUMBER}`;
const LandingHero = new URL("../../assets/landing-hero.jpg", import.meta.url)
  .href;
const SectionImg = new URL("../../assets/section2.jpg", import.meta.url).href;
const TestimonialImg = new URL("../../assets/landing-tes.jpg", import.meta.url)
  .href;

// ── SVG Icons ─────────────────────────────────────────────────────────────────

export default function UEC2026Page() {
  const formRef = useRef<HTMLDivElement>(null);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    if (typeof window !== "undefined" && (window as any).gtag) {
      (window as any).gtag("event", "cta_click", { event_category: "UEC2026" });
    }
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-white pt-20" style={{ fontFamily: FONT }}>
        {/* Floating WhatsApp */}
        {/* <a
        href={WA_LINK}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-green-500 flex items-center justify-center shadow-2xl shadow-green-500/40 hover:scale-110 transition-transform"
        onClick={() =>
          (window as any).gtag?.("event", "whatsapp_click", {
            event_category: "UEC2026",
          })
        }
      >
        <svg viewBox="0 0 24 24" className="w-7 h-7 fill-white">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      </a> */}

        {/* ── 1. HERO ──────────────────────────────────────────────────────────── */}
        <section className="relative overflow-hidden bg-[#0F2744] min-h-screen flex items-center">
          {/* Background decoration */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-amber-500/10 blur-3xl" />
            <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full bg-blue-400/10 blur-3xl" />
            <div
              className="absolute inset-0 opacity-5"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
                backgroundSize: "40px 40px",
              }}
            />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-24 w-full">
            <div className="grid lg:grid-cols-[1fr_400px] gap-8 lg:gap-20 items-center">
              {/* Left — copy */}
              <div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/20 border border-amber-500/30 mb-6">
                    <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                    <span
                      className="text-[13px] text-amber-300 uppercase tracking-widest"
                      style={{ fontWeight: 700 }}
                    >
                      100% 免费 · 在线直播 · Zoom
                    </span>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.55, delay: 0.08 }}
                >
                  <p
                    className="text-[13px] text-amber-400 uppercase tracking-[0.2em] mb-3"
                    style={{ fontWeight: 700 }}
                  >
                    2026 统考
                  </p>
                  <h1
                    className="text-[52px] lg:text-[68px] text-white leading-[1.05] mb-5 tracking-tight"
                    style={{ fontWeight: 900 }}
                  >
                    终极考试攻略班
                  </h1>
                  <p
                    className="text-[18px] text-white/65 mb-10 leading-relaxed"
                    style={{ fontWeight: 400 }}
                  >
                    掌握统考必考重点，让复习少走弯路！
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.15 }}
                  className="flex flex-wrap gap-3 mb-10"
                >
                  {[
                    {
                      icon: <Users className="w-3.5 h-3.5" />,
                      text: "8 位专业名师",
                    },
                    {
                      icon: <BookOpen className="w-3.5 h-3.5" />,
                      text: "8 科考试攻略",
                    },
                    {
                      icon: <Award className="w-3.5 h-3.5" />,
                      text: "100% 免费",
                    },
                  ].map((s, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-[13px]"
                      style={{ fontWeight: 600 }}
                    >
                      <span className="text-amber-400">{s.icon}</span> {s.text}
                    </div>
                  ))}
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="flex flex-col gap-2.5 mb-10 text-[15px]"
                >
                  {[
                    {
                      icon: <Calendar className="w-4 h-4" />,
                      text: "2026年9月21日 – 10月1日",
                    },
                    {
                      icon: <Clock className="w-4 h-4" />,
                      text: "傍晚 7:30 – 晚上 10:00",
                    },
                    {
                      icon: <Monitor className="w-4 h-4" />,
                      text: "Zoom Online 在线直播",
                    },
                  ].map((info, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-3 text-white/75"
                      style={{ fontWeight: 500 }}
                    >
                      <span className="text-amber-400">{info.icon}</span>{" "}
                      {info.text}
                    </div>
                  ))}
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.25 }}
                  className="flex flex-col sm:flex-row gap-3"
                >
                  <button
                    onClick={scrollToForm}
                    className="px-8 py-4 rounded-2xl bg-gradient-to-r from-amber-400 to-orange-500 text-white text-[15px] shadow-2xl shadow-amber-500/30 hover:shadow-amber-500/50 hover:-translate-y-1 transition-all flex items-center justify-center gap-2 cursor-pointer"
                    style={{ fontWeight: 800 }}
                  >
                    🎓 立即免费报名 <ArrowRight className="w-5 h-5" />
                  </button>
                  <a
                    href={WA_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-6 py-4 rounded-2xl border border-white/30 text-white text-[14px] hover:bg-white/10 transition-all flex items-center justify-center gap-2"
                    style={{ fontWeight: 600 }}
                  >
                    <MessageCircle className="w-4 h-4 text-green-400" />{" "}
                    WhatsApp 查询
                  </a>
                </motion.div>
              </div>

              {/* Right — student photo */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="hidden lg:block relative"
              >
                {/* Decorative ring behind card */}
                <div className="absolute inset-0 rounded-3xl bg-amber-500/10 blur-2xl scale-110" />
                {/* Photo card — fills the column */}
                <div className="relative w-full h-[520px] rounded-3xl overflow-hidden shadow-2xl shadow-black/50 border border-white/10">
                  <img
                    src={LandingHero}
                    alt="认真备考的学生"
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0F2744]/85 via-[#0F2744]/10 to-transparent" />
                  {/* Badge inside card at bottom */}
                  <div className="absolute bottom-5 left-5 right-5 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-4">
                    <p
                      className="text-white text-[14px]"
                      style={{ fontWeight: 700 }}
                    >
                      🏆 名师重点 · 免费笔记 · A1 技巧
                    </p>
                    <p
                      className="text-white/60 text-[12px] mt-1"
                      style={{ fontWeight: 400 }}
                    >
                      8 位老师，8 科全包，完全免费
                    </p>
                  </div>
                </div>
                {/* Floating stat — top-right corner of card */}
                <div className="absolute -top-4 -right-4 bg-white rounded-2xl shadow-xl px-4 py-3 border border-slate-100">
                  <p
                    className="text-[10px] text-slate-500 mb-1"
                    style={{ fontWeight: 500 }}
                  >
                    报名人数
                  </p>
                  <p
                    className="text-[24px] text-[#0F2744] leading-none"
                    style={{ fontWeight: 900 }}
                  >
                    500+
                  </p>
                  <p
                    className="text-[10px] text-amber-600 mt-1"
                    style={{ fontWeight: 600 }}
                  >
                    🔥 名额有限
                  </p>
                </div>
                {/* Floating subject — bottom-left corner of card */}
                <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-xl px-4 py-3 border border-slate-100">
                  <p
                    className="text-[10px] text-slate-500 mb-1"
                    style={{ fontWeight: 500 }}
                  >
                    涵盖科目
                  </p>
                  <p
                    className="text-[24px] text-[#0F2744] leading-none"
                    style={{ fontWeight: 900 }}
                  >
                    8 科
                  </p>
                  <p
                    className="text-[10px] text-green-600 mt-1"
                    style={{ fontWeight: 600 }}
                  >
                    ✓ 全免费
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ── 2. BENEFITS ───────────────────────────────────────────────────────── */}
        <section className="py-24 bg-white overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              {/* Left — image */}
              <motion.div
                initial={{ opacity: 0, x: -24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="relative hidden lg:block"
              >
                <div className="rounded-3xl overflow-hidden shadow-2xl shadow-slate-200">
                  <img
                    src={SectionImg}
                    alt="在线Zoom直播课"
                    className="w-full h-[560px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-[#0F2744]/30 to-transparent rounded-3xl" />
                </div>
                {/* Floating tag — 100% FREE */}
                <div className="absolute -bottom-5 -right-5 bg-amber-500 text-white rounded-2xl p-4 shadow-xl shadow-amber-500/30">
                  <p
                    className="text-[10px] uppercase tracking-widest mb-1"
                    style={{ fontWeight: 700 }}
                  >
                    完全免费
                  </p>
                  <p
                    className="text-[22px] leading-none"
                    style={{ fontWeight: 900 }}
                  >
                    100% FREE
                  </p>
                </div>
                <div className="absolute -top-4 -left-4 bg-white rounded-2xl shadow-lg p-3 border border-slate-100">
                  <p
                    className="text-[11px] text-slate-500 mb-0.5"
                    style={{ fontWeight: 500 }}
                  >
                    提供
                  </p>
                  <p
                    className="text-[14px] text-slate-900"
                    style={{ fontWeight: 800 }}
                  >
                    A1 考试笔记
                  </p>
                </div>
              </motion.div>

              {/* Right — benefits */}
              <div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="mb-10"
                >
                  <p
                    className="text-[13px] text-amber-600 uppercase tracking-widest mb-3"
                    style={{ fontWeight: 700 }}
                  >
                    为什么要参加？
                  </p>
                  <h2
                    className="text-[36px] lg:text-[44px] text-slate-900 tracking-tight leading-tight"
                    style={{ fontWeight: 900 }}
                  >
                    参加攻略班，你将获得：
                  </h2>
                </motion.div>
                <div className="flex flex-col gap-3">
                  {BENEFITS.map((b, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.07 }}
                      className="flex items-center gap-4 p-4 bg-slate-50 rounded-2xl border border-slate-100 hover:shadow-sm hover:-translate-y-0.5 transition-all"
                    >
                      <span className="text-[26px] flex-shrink-0 w-10 text-center">
                        {b.icon}
                      </span>
                      <div>
                        <h3
                          className="text-[15px] text-slate-900"
                          style={{ fontWeight: 800 }}
                        >
                          {b.title}
                        </h3>
                        <p
                          className="text-[13px] text-slate-500 leading-snug mt-0.5"
                          style={{ fontWeight: 400 }}
                        >
                          {b.desc}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── 3. PAIN POINTS ────────────────────────────────────────────────────── */}
        <section className="py-20 bg-[#0F2744]">
          <div className="max-w-5xl mx-auto px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <p
                className="text-[13px] text-amber-400 uppercase tracking-widest mb-3"
                style={{ fontWeight: 700 }}
              >
                你是否也有这些烦恼？
              </p>
              <h2
                className="text-[32px] lg:text-[40px] text-white tracking-tight"
                style={{ fontWeight: 900 }}
              >
                找不到重点？时间不够用？
                <br className="hidden sm:block" />
                答案不能拿满分？
              </h2>
            </motion.div>
            <div className="grid sm:grid-cols-3 gap-5">
              {PAIN_POINTS.map((p, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="rounded-2xl overflow-hidden"
                >
                  <div className="bg-red-500/20 border border-red-500/30 p-5 mb-0.5">
                    <p
                      className="text-[18px] text-white"
                      style={{ fontWeight: 800 }}
                    >
                      ❌ {p.pain}
                    </p>
                  </div>
                  <div className="bg-white/10 border border-white/20 p-5 rounded-b-2xl">
                    <div className="flex items-start gap-2.5">
                      <CheckCircle2 className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                      <p
                        className="text-[14px] text-white/85 leading-relaxed"
                        style={{ fontWeight: 500 }}
                      >
                        {p.solution}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-center mt-10"
            >
              <button
                onClick={scrollToForm}
                className="px-8 py-4 rounded-2xl bg-amber-500 text-white text-[15px] hover:bg-amber-400 transition-colors shadow-xl shadow-amber-500/20 cursor-pointer"
                style={{ fontWeight: 800 }}
              >
                立即报名，解决这些难题！
              </button>
            </motion.div>
          </div>
        </section>

        {/* ── 4. SCHEDULE ───────────────────────────────────────────────────────── */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-5xl mx-auto px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <p
                className="text-[13px] text-amber-600 uppercase tracking-widest mb-3"
                style={{ fontWeight: 700 }}
              >
                课程时间表
              </p>
              <h2
                className="text-[32px] lg:text-[40px] text-slate-900 tracking-tight"
                style={{ fontWeight: 900 }}
              >
                9 天课程安排
              </h2>
              <p
                className="text-[14px] text-slate-500 mt-2"
                style={{ fontWeight: 400 }}
              >
                每晚 7:30pm – 10:00pm · Zoom 在线直播
              </p>
            </motion.div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {SCHEDULE.map((s, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className={`rounded-2xl border overflow-hidden ${s.rest ? "bg-slate-100 border-slate-200 opacity-60" : "bg-white border-slate-100 shadow-sm"}`}
                >
                  <div
                    className="h-1.5"
                    style={{ backgroundColor: s.rest ? "#9E9E9E" : s.color }}
                  />
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span
                        className="text-[11px] text-slate-400 uppercase tracking-wider"
                        style={{ fontWeight: 600 }}
                      >
                        {s.day}
                      </span>
                      <span
                        className="text-[11px] text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full"
                        style={{ fontWeight: 500 }}
                      >
                        {s.date}
                      </span>
                    </div>
                    {s.rest ? (
                      <div className="flex flex-col items-center py-3 opacity-50">
                        <div className="w-10 h-10 mb-2">
                          {SUBJECT_ICONS["休息日"]}
                        </div>
                        <p
                          className="text-[14px] text-slate-400 text-center"
                          style={{ fontWeight: 600 }}
                        >
                          — 休息日 —
                        </p>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-3 mb-2">
                          <div
                            className="w-10 h-10 rounded-xl flex-shrink-0 p-2"
                            style={{ backgroundColor: s.color }}
                          >
                            {SUBJECT_ICONS[s.subject]}
                          </div>
                          <p
                            className="text-[15px] text-slate-900"
                            style={{ fontWeight: 800, color: s.color }}
                          >
                            {s.subject}
                          </p>
                        </div>
                        <p
                          className="text-[12px] text-slate-500 mb-2"
                          style={{ fontWeight: 500 }}
                        >
                          {s.teacher}
                        </p>
                        <div className="flex items-center gap-1.5">
                          <Clock className="w-3 h-3 text-slate-400" />
                          <span
                            className="text-[11px] text-slate-400"
                            style={{ fontWeight: 400 }}
                          >
                            7:30pm – 10:00pm
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── 5. TEACHERS ───────────────────────────────────────────────────────── */}
        <section className="py-20 bg-white">
          <div className="max-w-5xl mx-auto px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <p
                className="text-[13px] text-amber-600 uppercase tracking-widest mb-3"
                style={{ fontWeight: 700 }}
              >
                名师阵容
              </p>
              <h2
                className="text-[32px] lg:text-[40px] text-slate-900 tracking-tight"
                style={{ fontWeight: 900 }}
              >
                8 位专业名师，亲自为你出战
              </h2>
            </motion.div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {TEACHERS.map((t, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all"
                >
                  {/* Avatar */}
                  <div className="h-2" style={{ backgroundColor: t.color }} />
                  <div className="p-5">
                    <div className="flex items-center justify-center">
                      <img
                        src={t.avatar}
                        alt=""
                        className="w-full h-40 rounded-lg object-cover shadow-md"
                      />
                    </div>
                    <h3
                      className="text-[14px] text-slate-900 text-center mb-0.5"
                      style={{ fontWeight: 800 }}
                    >
                      {t.name}
                    </h3>
                    <p
                      className="text-[11px] text-center mb-4 px-2 py-1 rounded-full mx-auto w-fit text-white"
                      style={{
                        fontWeight: 700,
                        backgroundColor: t.color,
                        fontSize: "10px",
                      }}
                    >
                      {t.subject}
                    </p>
                    <div className="flex flex-col gap-1.5">
                      {t.credentials.slice(0, 3).map((c, ci) => (
                        <div key={ci} className="flex items-start gap-1.5">
                          <div
                            className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0"
                            style={{ backgroundColor: t.color }}
                          />
                          <p
                            className="text-[11px] text-slate-500 leading-snug"
                            style={{ fontWeight: 400 }}
                          >
                            {c}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="max-w-5xl mx-auto md:px-8 mt-10 flex flex-col items-center justify-center gap-12">
              <h2
                className="text-[32px] lg:text-[40px] text-slate-900 tracking-tight"
                style={{ fontWeight: 900 }}
              >
                教学内容
              </h2>
              <Carousel
                items={CLASS_SCREENSHOT}
                itemsPerPage={1}
                enableMobilePopout
                renderItem={(image, index) => (
                  <div className="md:h-[420px] w-full lg:w-[700px] overflow-hidden">
                    <img
                      src={image}
                      alt={`Class preview ${index + 1}`}
                      className="w-full h-full object-fill"
                    />
                  </div>
                )}
                renderPopoutItem={(image, index) => (
                  <img
                    src={image}
                    alt={`Class preview ${index + 1}`}
                    className="max-h-[88vh] w-full object-contain"
                  />
                )}
              />
            </div>

            <div className="w-full flex items-center justify-center mt-10">
              <button
                onClick={scrollToForm}
                className="px-10 py-4 rounded-2xl bg-gradient-to-r from-amber-400 to-orange-500 text-white text-[16px] shadow-xl shadow-amber-500/30 hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 cursor-pointer"
                style={{ fontWeight: 800 }}
              >
                🎓 立即免费报名
              </button>
            </div>
          </div>
        </section>

        {/* ── 6. TESTIMONIALS ───────────────────────────────────────────────────── */}
        <section className="py-20 bg-slate-50 relative overflow-hidden">
          {/* Background image with overlay */}
          <div className="absolute inset-0 pointer-events-none">
            <img
              src={TestimonialImg}
              alt=""
              className="w-full h-full object-cover opacity-5"
            />
          </div>
          <div className="max-w-5xl mx-auto px-6 lg:px-8 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <p
                className="text-[13px] text-amber-600 uppercase tracking-widest mb-3"
                style={{ fontWeight: 700 }}
              >
                学生真实反馈
              </p>
              <h2
                className="text-[32px] lg:text-[40px] text-slate-900 tracking-tight"
                style={{ fontWeight: 900 }}
              >
                他们都说好！
              </h2>
            </motion.div>
            <div className="grid sm:grid-cols-2 gap-5">
              {TESTIMONIALS.map((t, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6"
                >
                  <StarRating count={t.stars} />
                  <p
                    className="text-[14px] text-slate-600 leading-relaxed my-4"
                    style={{ fontWeight: 400 }}
                  >
                    "{t.text}"
                  </p>
                  <div className="flex items-center gap-3 pt-3 border-t border-slate-100">
                    <div
                      className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center text-amber-700 text-[13px]"
                      style={{ fontWeight: 800 }}
                    >
                      {t.name.charAt(0)}
                    </div>
                    <div>
                      <p
                        className="text-[13px] text-slate-900"
                        style={{ fontWeight: 700 }}
                      >
                        {t.name}
                      </p>
                      <p
                        className="text-[11px] text-slate-400"
                        style={{ fontWeight: 400 }}
                      >
                        {t.role}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── 7. 报名流程 ───────────────────────────────────────────────────────── */}
        <section className="py-20 bg-[#0F2744]">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-14"
            >
              <p
                className="text-[13px] text-amber-400 uppercase tracking-widest mb-3"
                style={{ fontWeight: 700 }}
              >
                报名流程
              </p>
              <h2
                className="text-[32px] lg:text-[40px] text-white tracking-tight"
                style={{ fontWeight: 900 }}
              >
                3 步完成报名
              </h2>
            </motion.div>

            <div className="relative">
              {/* connector line */}
              <div className="hidden lg:block absolute top-12 left-1/2 -translate-x-1/2 w-[70%] h-0.5 bg-white/10" />

              <div className="grid lg:grid-cols-3 gap-8">
                {[
                  {
                    step: "01",
                    title: "填写报名表",
                    desc: "选择科目并提交报名表，只需 2 分钟完成。",
                    svg: (
                      <svg
                        viewBox="0 0 64 64"
                        fill="none"
                        className="w-14 h-14"
                      >
                        <rect
                          x="10"
                          y="8"
                          width="36"
                          height="44"
                          rx="5"
                          fill="white"
                          fillOpacity="0.15"
                          stroke="white"
                          strokeWidth="1.5"
                        />
                        <line
                          x1="18"
                          y1="20"
                          x2="38"
                          y2="20"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <line
                          x1="18"
                          y1="28"
                          x2="38"
                          y2="28"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <line
                          x1="18"
                          y1="36"
                          x2="30"
                          y2="36"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <circle cx="46" cy="46" r="10" fill="#F59E0B" />
                        <path
                          d="M42 46 L45 49 L51 43"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ),
                  },
                  {
                    step: "02",
                    title: "加入 WhatsApp 群组",
                    desc: "提交后自动跳转 WhatsApp，点击「加入群组」即可加入备考群。",
                    svg: (
                      <svg
                        viewBox="0 0 64 64"
                        fill="none"
                        className="w-14 h-14"
                      >
                        <rect
                          x="14"
                          y="8"
                          width="30"
                          height="48"
                          rx="6"
                          fill="white"
                          fillOpacity="0.15"
                          stroke="white"
                          strokeWidth="1.5"
                        />
                        <circle
                          cx="29"
                          cy="50"
                          r="2"
                          fill="white"
                          fillOpacity="0.6"
                        />
                        <rect
                          x="20"
                          y="14"
                          width="18"
                          height="28"
                          rx="3"
                          fill="white"
                          fillOpacity="0.1"
                        />
                        <circle cx="46" cy="20" r="10" fill="#25D366" />
                        <path
                          d="M41 20 C41 17.2 43.2 15 46 15 C48.8 15 51 17.2 51 20 C51 22.8 48.8 25 46 25 L41 27 L43 22 C41.8 21.4 41 20.8 41 20Z"
                          fill="white"
                        />
                      </svg>
                    ),
                  },
                  {
                    step: "03",
                    title: "领取学习资料",
                    desc: "SCS 辅导员联系您后，即可领取 Zoom 链接、考试笔记、课程回放及最新通知。",
                    svg: (
                      <svg
                        viewBox="0 0 64 64"
                        fill="none"
                        className="w-14 h-14"
                      >
                        <path
                          d="M12 20 L12 50 Q12 52 14 52 L50 52 Q52 52 52 50 L52 26 L38 26 L38 20 Z"
                          fill="white"
                          fillOpacity="0.15"
                          stroke="white"
                          strokeWidth="1.5"
                        />
                        <path
                          d="M38 20 L38 26 L52 26 L44 18 L38 20Z"
                          fill="white"
                          fillOpacity="0.3"
                          stroke="white"
                          strokeWidth="1"
                        />
                        <line
                          x1="20"
                          y1="36"
                          x2="44"
                          y2="36"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <line
                          x1="20"
                          y1="42"
                          x2="36"
                          y2="42"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <circle cx="46" cy="18" r="10" fill="#F59E0B" />
                        <path
                          d="M46 13 L46 23 M41 18 L51 18"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                    ),
                  },
                ].map((s, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.12 }}
                    className="flex flex-col items-center text-center"
                  >
                    <div className="w-28 h-28 rounded-3xl bg-white/10 border border-white/20 flex flex-col items-center justify-center mb-5 relative">
                      <span
                        className="text-[9px] text-amber-400 uppercase tracking-widest mb-2"
                        style={{ fontWeight: 700 }}
                      >
                        Step {s.step}
                      </span>
                      {s.svg}
                    </div>
                    <h3
                      className="text-[17px] text-white mb-2"
                      style={{ fontWeight: 800 }}
                    >
                      {s.title}
                    </h3>
                    <p
                      className="text-[13px] text-white/60 leading-relaxed"
                      style={{ fontWeight: 400 }}
                    >
                      {s.desc}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── 8. FAQ ────────────────────────────────────────────────────────────── */}
        <section className="py-20 bg-[#0F2744] border-t border-white/5">
          <div className="max-w-2xl mx-auto px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-10"
            >
              <p
                className="text-[13px] text-amber-400 uppercase tracking-widest mb-3"
                style={{ fontWeight: 700 }}
              >
                常见问题
              </p>
              <h2
                className="text-[30px] text-white tracking-tight"
                style={{ fontWeight: 900 }}
              >
                FAQ
              </h2>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white/5 border border-white/10 rounded-2xl px-6"
            >
              {FAQS.map((faq, i) => (
                <FAQItem key={i} q={faq.q} a={faq.a} index={i} />
              ))}
            </motion.div>
          </div>
        </section>

        {/* ── 9. REGISTRATION FORM ──────────────────────────────────────────────── */}
        <section className="py-20 bg-white" ref={formRef} id="register">
          <div className="max-w-2xl mx-auto px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-8"
            >
              <p
                className="text-[13px] text-amber-600 uppercase tracking-widest mb-3"
                style={{ fontWeight: 700 }}
              >
                立即报名
              </p>
              <h2
                className="text-[32px] text-slate-900 tracking-tight mb-2"
                style={{ fontWeight: 900 }}
              >
                免费报名，名额有限！
              </h2>
              <p
                className="text-[14px] text-slate-500"
                style={{ fontWeight: 400 }}
              >
                先到先得，立即锁定你的位置！🔥
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-3xl border border-slate-100 shadow-xl shadow-slate-200/60 overflow-hidden"
            >
              <div className="bg-gradient-to-r from-[#0F2744] to-[#1a3a60] p-5 text-center">
                <p
                  className="text-[16px] text-white"
                  style={{ fontWeight: 800 }}
                >
                  《2026 统考终极考试攻略班》
                </p>
                <p
                  className="text-[13px] text-white/60 mt-1"
                  style={{ fontWeight: 400 }}
                >
                  21 Sep – 1 Oct 2026 · 7:30pm – 10:00pm · Zoom
                </p>
              </div>
              <RegistrationForm id="registration-form" />
            </motion.div>
          </div>
        </section>

        {/* ── 10. FINAL CTA ─────────────────────────────────────────────────────── */}
        <section className="py-20 bg-gradient-to-br from-[#0F2744] to-[#1a3a60]">
          <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <p
                className="text-[13px] text-amber-400 uppercase tracking-widest mb-4"
                style={{ fontWeight: 700 }}
              >
                报名不只是上课，更获得完整升学资源！
              </p>
              <h2
                className="text-[30px] lg:text-[40px] text-white tracking-tight mb-6"
                style={{ fontWeight: 900 }}
              >
                报名后，你还将获得：
              </h2>
              <div className="grid sm:grid-cols-2 gap-3 max-w-xl mx-auto mb-10 text-left">
                {[
                  "统考后升学资讯",
                  "奖学金申请资讯",
                  "大学科系咨询",
                  "升学规划建议",
                ].map((item, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 bg-white/10 rounded-xl px-4 py-3"
                  >
                    <CheckCircle2 className="w-4 h-4 text-amber-400 flex-shrink-0" />
                    <span
                      className="text-[14px] text-white"
                      style={{ fontWeight: 600 }}
                    >
                      {item}
                    </span>
                  </div>
                ))}
              </div>
              <p
                className="text-[15px] text-white/70 mb-8"
                style={{ fontWeight: 400 }}
              >
                一次报名，考试冲刺与升学准备同步进行！
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={scrollToForm}
                  className="px-10 py-4 rounded-2xl bg-gradient-to-r from-amber-400 to-orange-500 text-white text-[16px] shadow-xl shadow-amber-500/30 hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 cursor-pointer"
                  style={{ fontWeight: 800 }}
                >
                  🎓 立即免费报名
                </button>
                <a
                  href={WA_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 py-4 rounded-2xl bg-green-500 text-white text-[15px] hover:bg-green-400 transition-colors flex items-center justify-center gap-2"
                  style={{ fontWeight: 700 }}
                >
                  <MessageCircle className="w-4 h-4" /> WhatsApp Admin
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-8 bg-[#080f1d] text-center">
          <p
            className="text-[13px] text-white/40"
            style={{ fontFamily: FONT, fontWeight: 400 }}
          >
            © 2026 SCS Education · 本活动完全免费 · 如有疑问请联系{" "}
            <a
              href={WA_LINK}
              className="text-amber-400 hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              WhatsApp Admin
            </a>
          </p>
        </footer>
      </div>
    </>
  );
}
